
import { computed, defineComponent, onBeforeMount, ref } from "vue";

import BookingOverviewActive from "@/components/Booking/Overview/BookingOverviewActive.vue";
import BookingOverviewNoQuery from "@/components/Booking/Overview/BookingOverviewNoQuery.vue";
import SearchComponentHotel from "./components/Search/SearchComponentHotel.vue";
import DotSpinner from "./components/EasySpinners/DotSpinner.vue";

import EasyAlert from "@/components/EasyAlert.vue";

import { useStore } from "vuex";

import applicationService from "./services/application.service";
import IResponseData from "./types/IResponseData";

import {
  SearchBookingOverview,
  SearchHotelRoom,
  SearchTravelBookingSelection,
} from "./types/models";
import TravelService from "@/services/travel.service";

export default defineComponent({
  name: "BookingOverview",
  components: {
    BookingOverviewActive,
    BookingOverviewNoQuery,
    SearchComponentHotel,
    EasyAlert,
    DotSpinner
  },
  setup() {
    const store = useStore();

    const pageUrls = computed(() => store.getters["ApplicationModule/getPageUrls"]);
    const pageTexts = computed(() => store.getters["ApplicationModule/getPageTexts"]);
    const localeStrings = computed(() => store.getters["ApplicationModule/getLocaleStrings"]);

    const isLoading = ref(true);
    const previousViewRoute = ref();
    const hasBookingOverview = ref(false);
    const isShowAlert = ref(false);
    const alertMessage = ref("");

    const showSearchComponent = ref(false);

    const clickDatePicker = () => {
      document.getElementById('searchDatepickerBtn')?.click();
      document.getElementById('searchDatepickerBtn')?.scrollIntoView({behavior: "smooth", block: "center"});
    }

    const bookingOverview = computed(
      () =>
        store.getters[
        "TravelSearchBookingModule/getSearchTravelBookingOverview"
        ]
    );

    const selectedRooms = computed(() => {
      let rooms = Array<SearchHotelRoom>();

      if (bookingOverview.value?.paxRooms) {
        for (const paxRoom of bookingOverview.value.paxRooms) {
          const selectedRoom = paxRoom.rooms.find((room) => {
            return room.isSelected;
          });
          if (selectedRoom) rooms.push(selectedRoom);
        }
      }

      return rooms;
    });

    const searchQuery = computed(() => {
      let newSearchQuery = {};

      if (bookingOverview.value?.bookingSelection) {
        for (const [key, value] of Object.entries(
          bookingOverview.value.bookingSelection
        )) {
          if (typeof value === "string") {
            newSearchQuery[key] = value;
          }
        }
      }

      return newSearchQuery;
    });

    const isQueryParamsSameAsBookingoverview = (
      queryParams: URLSearchParams
    ) => {
      for (const [key, value] of queryParams) {
        if (bookingOverview.value.bookingSelection[key] !== value) return false;
      }
      return true;
    };

    onBeforeMount(() => {
      if (!store.getters["ApplicationModule/getPageUrls"]) {
        applicationService
          .getPageUrls()
          .then((response: IResponseData<{ [key: string]: string }>) => {
            store.dispatch(
              "ApplicationModule/updatePageUrls",
              response.data
            );
          });
      }

      if (!store.getters["ApplicationModule/getPageTexts"]) {
        applicationService
          .getPageTexts()
          .then((response: IResponseData<{ [key: string]: string }>) => {
            store.dispatch(
              "ApplicationModule/updatePageTexts",
              response.data
            );
          });
      }

      store.dispatch(
        "ApplicationModule/updateLocaleStrings",
        document.documentElement.getAttribute('lang')
      );

      document.getElementById("bookingApp")?.classList.remove("show-search");

      const queryParams = new URLSearchParams(
        decodeURI("?" + window.location.hash.split("?")[1])
      );
      // If query params are not the same as in store update with query values
      if (
        queryParams.has("searchKey") &&
        bookingOverview.value &&
        bookingOverview.value?.bookingSelection
      ) {
        if (isQueryParamsSameAsBookingoverview(queryParams)) {
          hasBookingOverview.value = true;
        } else {
          updateBookingOverviewFromQueryParams(queryParams);
        }
      } else if (queryParams.has("searchKey")) {
        updateBookingOverviewFromQueryParams(queryParams);
      } else {
        store.dispatch("TravelSearchResponseModule/deleteSearchTravelResponse");
        store.dispatch("TravelSearchBookingModule/deleteSearchTravelBookingOverview");
        hasBookingOverview.value = true;
        showSearchComponent.value = true;
        document.getElementById("bookingApp")?.classList.add("show-search");
      }
    });

    const updateBookingOverviewFromQueryParams = (
      queryParams: URLSearchParams
    ) => {
      const newBookingSelection = {} as SearchTravelBookingSelection;
      for (const [key, value] of queryParams) {
        newBookingSelection[key] = value;
      }
      TravelService.getSearchTravelOverview(newBookingSelection).then(
        (response: IResponseData<SearchBookingOverview>) => {
          if (response.data.success) {
            store
              .dispatch(
                "TravelSearchBookingModule/updateSearchTravelBookingOverview",
                response.data.result
              )
              .then(() => {
                hasBookingOverview.value = true;
              });
          } else {
            store.dispatch("TravelSearchResponseModule/deleteSearchTravelResponse");
            store.dispatch("TravelSearchBookingModule/deleteSearchTravelBookingOverview");
            alertMessage.value = response.data.exception.message;
            isShowAlert.value = true;
            hasBookingOverview.value = true;
          }
        }
      );
    };

    const closeAlert = () => {
      isShowAlert.value = false;
    }

    return {
      clickDatePicker,
      hasBookingOverview,
      isLoading,
      bookingOverview,
      selectedRooms,
      searchRequest: computed(
        () => store.getters["TravelSearchModule/getSearchTravelRequest"]
      ),
      searchQuery,
      previousViewRoute,
      pageUrls,
      pageTexts,
      localeStrings,
      isShowAlert,
      alertMessage,
      closeAlert,
      showSearchComponent
    };
  },
});
